import React, { createContext, useContext, useCallback, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiFetch } from '../api/client';

// Suivi de progression DB-driven : items de séquence (et chapters) complétés par
// l'utilisateur, par mission. Source de vérité = base (assignment.completedItemsJSON).
// Partagé entre ChapterDetail / TaskDetail / sous-écrans pour un déblocage cohérent.

interface MissionProgress {
  items: Set<string>;
  chapters: Set<string>;
}

interface StoryProgressContextType {
  getCompletedItems: (missionId: number) => Set<string>;
  isItemCompleted: (missionId: number, itemId: string) => boolean;
  loadProgress: (missionId: number) => Promise<void>;
  completeItem: (missionId: number, itemId: string) => Promise<void>;
}

const EMPTY: MissionProgress = { items: new Set(), chapters: new Set() };
const StoryProgressContext = createContext<StoryProgressContextType | undefined>(undefined);

export function StoryProgressProvider({ children }: { children: ReactNode }) {
  const { user, getAccessToken } = useAuth();
  const [byMission, setByMission] = useState<Record<number, MissionProgress>>({});

  const getCompletedItems = useCallback(
    (missionId: number) => byMission[missionId]?.items ?? EMPTY.items,
    [byMission],
  );

  const isItemCompleted = useCallback(
    (missionId: number, itemId: string) => !!byMission[missionId]?.items.has(itemId),
    [byMission],
  );

  // Charge la progression de la mission depuis la base.
  const loadProgress = useCallback(async (missionId: number) => {
    const token = getAccessToken();
    if (!user || !token) return;
    try {
      const data = await apiFetch<{ completedItems?: string[]; completedChapters?: string[] }>(
        `/me/stories/${missionId}/progress`, { token, auth: true },
      );
      setByMission(prev => ({
        ...prev,
        [missionId]: {
          items: new Set((data?.completedItems ?? []).map(String)),
          chapters: new Set((data?.completedChapters ?? []).map(String)),
        },
      }));
    } catch (_) { /* offline -> garde l'état courant */ }
  }, [user, getAccessToken]);

  // The server decides whether viewing this element actually completes it.
  const completeItem = useCallback(async (missionId: number, itemId: string) => {
    const id = String(itemId);
    const token = getAccessToken();
    if (!user || !token) return;
    try {
      const data = await apiFetch<{ completedItems: string[] }>(`/me/stories/${missionId}/items/${encodeURIComponent(id)}/complete`, {
        method: 'POST', token, auth: true,
      });
      if (data?.completedItems) setByMission(prev => ({
        ...prev,
        [missionId]: { items: new Set(data.completedItems.map(String)),
                       chapters: prev[missionId]?.chapters ?? new Set<string>() },
      }));
    } catch (_) { /* An unconfirmed action never unlocks the next element. */ }
  }, [user, getAccessToken]);

  return (
    <StoryProgressContext.Provider value={{ getCompletedItems, isItemCompleted, loadProgress, completeItem }}>
      {children}
    </StoryProgressContext.Provider>
  );
}

export function useStoryProgress() {
  const ctx = useContext(StoryProgressContext);
  if (!ctx) throw new Error('useStoryProgress must be used within a StoryProgressProvider');
  return ctx;
}
